import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { attendanceAPI, holidayAPI, moodAPI } from '../../../services';
import { useApi, useAsyncAction } from '../../../hooks';
import {
  Button,
  LoadingSpinner,
  Badge,
  ErrorMessage,
  SuccessMessage,
  Modal,
} from '../../UI';
import { Clock, CheckCircle2, XCircle, Calendar, ChevronLeft, ChevronRight, MapPin, Camera, RefreshCw } from 'lucide-react';
import AttendanceCalendar from '../AttendanceCalendar';

const MOOD_EMOJI = ['😢','😕','😐','😊','😄'];
const MOOD_LABEL = ['Very Sad','Sad','Neutral','Happy','Very Happy'];

const AttendancePage = () => {
  const { user } = useAuth();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showMoodPopup, setShowMoodPopup] = useState(false);
  const [moodScore, setMoodScore] = useState(null);
  const [moodNote, setMoodNote] = useState('');
  const [moodStatus, setMoodStatus] = useState(null);
  const [moodSubmitting, setMoodSubmitting] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  // Camera states
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [punchType, setPunchType] = useState(null); // 'in' or 'out'
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  
  const { data: todayData, loading: todayLoading, refetch: refetchToday } = useApi(
    attendanceAPI.getTodayAttendance,
    []
  );
  
  const { data: historyData, loading: historyLoading, refetch: refetchHistory } = useApi(
    () => attendanceAPI.getMyHistory({ month: selectedMonth, year: selectedYear }),
    [selectedMonth, selectedYear]
  );

  const { data: holidayData, loading: holidaysLoading, refetch: refetchHolidays } = useApi(
    () => holidayAPI.getHolidays({ year: selectedYear, month: selectedMonth }),
    [selectedMonth, selectedYear]
  );

  const { data: regularizationData, loading: regularizationsLoading, refetch: refetchRegularizations } = useApi(
    () => attendanceAPI.getMyRegularizations({ month: selectedMonth, year: selectedYear }),
    [selectedMonth, selectedYear]
  );

  const { data: revokeData, refetch: refetchRevokes } = useApi(
    () => attendanceAPI.getMyRevokes({ month: selectedMonth, year: selectedYear }),
    [selectedMonth, selectedYear]
  );

  const { loading: punchLoading, execute: executePunch } = useAsyncAction();

  const todayAttendance = todayData?.attendance;
  const hasPunchedIn = todayData?.hasPunchedIn || false;
  const hasPunchedOut = todayData?.hasPunchedOut || false;
  const attendanceHistory = historyData?.attendance || [];
  const stats = historyData?.stats || {};
  const holidays = holidayData?.holidays || [];
  const workingDays = historyData?.workingDays || [1, 2, 3, 4, 5];
  const regularizations = regularizationData?.regularizations || [];
  const revokes = revokeData?.revokes || [];
  const shiftInfo = historyData?.shiftInfo || null;

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    refetchHistory();
  }, [selectedMonth, selectedYear]);

  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        }),
        (error) => reject(error),
        { enableHighAccuracy: true, timeout: 10000 }
      );
    });
  };

  // Camera functions
  const startCamera = async () => {
    try {
      setCameraError('');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: 640, height: 480 }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
        setIsCameraReady(true);
      }
    } catch (err) {
      console.error('Camera error:', err);
      setCameraError('Unable to access camera. Please allow camera permission.');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setIsCameraReady(false);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
      setCapturedPhoto(dataUrl);
    }
  };

  const retakePhoto = () => {
    setCapturedPhoto(null);
  };

  const openCameraModal = (type) => {
    setPunchType(type);
    setCapturedPhoto(null);
    setCameraError('');
    setShowCameraModal(true);
    setTimeout(() => startCamera(), 100);
  };

  const closeCameraModal = () => {
    stopCamera();
    setShowCameraModal(false);
    setPunchType(null);
    setCapturedPhoto(null);
  };

  const handlePunch = async (type) => {
    try {
      setError('');
      const location = await getCurrentLocation();
      const result = await executePunch(
        type === 'in' ? attendanceAPI.punchIn : attendanceAPI.punchOut,
        { location, photo: capturedPhoto }
      );
      if (result.success) {
        setSuccess(`Punch ${type} successful!`);
        closeCameraModal();
        refetchToday();
        setTimeout(() => setSuccess(''), 3000);
        if (type === 'out') {
          const clientModules = user?.client?.subscription?.modules || {};
          if (clientModules.moodCheckin === true) {
            try {
              const res = await moodAPI.getTodayStatus();
              if (!res?.data?.submitted) {
                setMoodStatus(res?.data || null);
                setMoodScore(null);
                setMoodNote('');
                setShowMoodPopup(true);
              }
            } catch { /* mood check is optional, ignore errors */ }
          }
        }
      } else {
        setError(result.error || `Failed to punch ${type}`);
      }
    } catch (err) {
      setError(err.message || 'Failed to get location');
    }
  };

  const handleMoodSubmit = async () => {
    if (!moodScore) return;
    try {
      setMoodSubmitting(true);
      await moodAPI.submitMood({ score: moodScore, note: moodNote });
    } catch { /* ignore */ }
    finally { setMoodSubmitting(false); setShowMoodPopup(false); }
  };

  const handleMoodSkip = async () => {
    try {
      setMoodSubmitting(true);
      await moodAPI.skipMood();
    } catch { /* ignore */ }
    finally { setMoodSubmitting(false); setShowMoodPopup(false); }
  };

  const confirmPunch = async () => {
    if (!capturedPhoto) {
      setCameraError('Please capture a photo first');
      return;
    }
    await handlePunch(punchType);
  };

  const formatTime = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' });
  };

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const goToPrevMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  return (
    <div className="space-y-6">
      {error && <ErrorMessage message={error} onClose={() => setError('')} />}
      {success && <SuccessMessage message={success} onClose={() => setSuccess('')} />}

      <div>
        <h2 className="text-xl font-bold text-gray-900">Attendance</h2>
        <p className="text-gray-500">Track your daily attendance and work hours</p>
      </div>

      {/* Current Time & Punch */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-6 text-white">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-center md:text-left">
            <p className="text-blue-100 mb-1">Current Time</p>
            <p className="text-4xl font-bold font-mono">
              {currentTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'Asia/Kolkata' })}
            </p>
            <p className="text-blue-100 mt-1">
              {currentTime.toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
          
          <div className="flex gap-4">
            {!hasPunchedIn ? (
              <Button
                onClick={() => openCameraModal('in')}
                loading={punchLoading}
                className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-3"
              >
                <Camera className="w-5 h-5" />
                Punch In
              </Button>
            ) : !hasPunchedOut ? (
              <Button
                onClick={() => openCameraModal('out')}
                loading={punchLoading}
                className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-6 py-3"
              >
                <Camera className="w-5 h-5" />
                Punch Out
              </Button>
            ) : (
              <div className="text-center bg-white/20 rounded-xl px-6 py-3">
                <CheckCircle2 className="w-6 h-6 mx-auto mb-1" />
                <p className="text-sm">Completed for today</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Today's Status */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-blue-600" />
          Today's Attendance
        </h3>
        
        {todayLoading ? (
          <div className="flex justify-center py-8"><LoadingSpinner /></div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-green-50 rounded-xl text-center">
              <p className="text-sm text-gray-500 mb-1">Punch In</p>
              {todayAttendance?.punchIn?.location?.latitude ? (
                <button
                  onClick={() => window.open(`https://www.google.com/maps?q=${todayAttendance.punchIn.location.latitude},${todayAttendance.punchIn.location.longitude}`, '_blank', 'noopener,noreferrer')}
                  className="text-xl font-bold text-green-600 hover:text-green-800 hover:underline cursor-pointer flex items-center justify-center gap-1 mx-auto"
                  title="View punch-in location on Google Maps"
                >
                  {formatTime(todayAttendance.punchIn.time)}
                  <MapPin className="w-4 h-4" />
                </button>
              ) : (
                <p className="text-xl font-bold text-green-600">
                  {todayAttendance?.punchIn ? formatTime(todayAttendance.punchIn.time) : '--:--'}
                </p>
              )}
            </div>
            <div className="p-4 bg-red-50 rounded-xl text-center">
              <p className="text-sm text-gray-500 mb-1">Punch Out</p>
              {todayAttendance?.punchOut?.location?.latitude ? (
                <button
                  onClick={() => window.open(`https://www.google.com/maps?q=${todayAttendance.punchOut.location.latitude},${todayAttendance.punchOut.location.longitude}`, '_blank', 'noopener,noreferrer')}
                  className="text-xl font-bold text-red-600 hover:text-red-800 hover:underline cursor-pointer flex items-center justify-center gap-1 mx-auto"
                  title="View punch-out location on Google Maps"
                >
                  {formatTime(todayAttendance.punchOut.time)}
                  <MapPin className="w-4 h-4" />
                </button>
              ) : (
                <p className="text-xl font-bold text-red-600">
                  {todayAttendance?.punchOut ? formatTime(todayAttendance.punchOut.time) : '--:--'}
                </p>
              )}
            </div>
            <div className="p-4 bg-blue-50 rounded-xl text-center">
              <p className="text-sm text-gray-500 mb-1">Work Hours</p>
              <p className="text-xl font-bold text-blue-600">
                {(() => {
                  const hours = todayAttendance?.workHours || (todayAttendance?.workDuration ? todayAttendance.workDuration / 60 : 0);
                  return hours && !isNaN(hours) ? `${Number(hours).toFixed(1)}h` : '0h';
                })()}
              </p>
            </div>
            <div className="p-4 bg-purple-50 rounded-xl text-center">
              <p className="text-sm text-gray-500 mb-1">Status</p>
              <Badge variant={todayAttendance?.status === 'present' ? 'success' : todayAttendance?.status === 'late' ? 'warning' : 'secondary'}>
                {todayAttendance?.status || 'Not marked'}
              </Badge>
            </div>
          </div>
        )}
      </div>

      {/* Monthly Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Present Days</p>
          <p className="text-2xl font-bold text-green-600">{stats.presentDays || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Absent Days</p>
          <p className="text-2xl font-bold text-red-600">{stats.absentDays || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Late Arrivals</p>
          <p className="text-2xl font-bold text-yellow-600">{stats.lateDays || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Avg Work Hours</p>
          <p className="text-2xl font-bold text-blue-600">{stats.avgWorkHours && !isNaN(stats.avgWorkHours) ? Number(stats.avgWorkHours).toFixed(1) : 0}h</p>
        </div>
      </div>

      {/* Month Selector & Calendar */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            Attendance History
          </h3>
          <div className="flex items-center gap-4">
            <button onClick={goToPrevMonth} className="p-2 hover:bg-gray-100 rounded-lg">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="font-medium text-gray-900">
              {months[selectedMonth - 1]} {selectedYear}
            </span>
            <button onClick={goToNextMonth} className="p-2 hover:bg-gray-100 rounded-lg">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {historyLoading ? (
          <div className="flex justify-center py-12"><LoadingSpinner /></div>
        ) : (
          <AttendanceCalendar
            attendance={attendanceHistory}
            holidays={holidays}
            month={selectedMonth}
            year={selectedYear}
            workingDays={workingDays}
            employeeStartDate={user?.dateOfJoining}
            regularizations={regularizations}
            revokes={revokes}
            shiftInfo={shiftInfo}
            onRegularizationApplied={() => refetchRegularizations()}
            onRevokeApplied={() => refetchRevokes()}
            onMonthChange={(newMonth, newYear) => {
              setSelectedMonth(newMonth);
              setSelectedYear(newYear);
            }}
          />
        )}
      </div>

      {/* Camera Modal */}
      <Modal
        isOpen={showCameraModal}
        onClose={closeCameraModal}
        title={`Punch ${punchType === 'in' ? 'In' : 'Out'} - Capture Photo`}
        size="md"
      >
        <div className="space-y-4">
          {cameraError && (
            <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
              {cameraError}
            </div>
          )}

          <div className="relative bg-black rounded-xl overflow-hidden aspect-[4/3]">
            {!capturedPhoto ? (
              <>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                />
                {!isCameraReady && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                    <LoadingSpinner />
                  </div>
                )}
              </>
            ) : (
              <img src={capturedPhoto} alt="Captured" className="w-full h-full object-cover" />
            )}
          </div>

          <canvas ref={canvasRef} className="hidden" />

          <div className="flex justify-center gap-4">
            {!capturedPhoto ? (
              <Button
                onClick={capturePhoto}
                disabled={!isCameraReady}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3"
              >
                <Camera className="w-5 h-5" />
                Capture Photo
              </Button>
            ) : (
              <>
                <Button
                  onClick={retakePhoto}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Retake
                </Button>
                <Button
                  onClick={confirmPunch}
                  loading={punchLoading}
                  className={`flex items-center gap-2 ${
                    punchType === 'in' 
                      ? 'bg-green-600 hover:bg-green-700' 
                      : 'bg-red-600 hover:bg-red-700'
                  } text-white px-6 py-3`}
                >
                  {punchType === 'in' ? (
                    <CheckCircle2 className="w-5 h-5" />
                  ) : (
                    <XCircle className="w-5 h-5" />
                  )}
                  Confirm Punch {punchType === 'in' ? 'In' : 'Out'}
                </Button>
              </>
            )}
          </div>

          <p className="text-xs text-gray-500 text-center">
            Your photo will be captured for attendance verification
          </p>
        </div>
      </Modal>

      {/* Mood Check-In Popup — shown after punch-out */}
      {showMoodPopup && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
            <p className="text-2xl mb-1">😊</p>
            <h2 className="text-lg font-bold text-gray-900 mb-1">Before you wrap up…</h2>
            <p className="text-sm text-gray-500 mb-5">How are you feeling today?</p>
            <div className="flex justify-center gap-3 mb-5">
              {[1,2,3,4,5].map(s => (
                <button key={s} onClick={() => setMoodScore(s)} className={`flex flex-col items-center p-2 rounded-xl border-2 transition-all ${moodScore===s?'border-blue-500 bg-blue-50':'border-gray-200 hover:border-blue-200'}`}>
                  <span className="text-2xl">{MOOD_EMOJI[s-1]}</span>
                  <span className="text-xs text-gray-500 mt-0.5">{s}</span>
                </button>
              ))}
            </div>
            {moodScore && <p className="text-sm font-medium text-blue-700 mb-3">{MOOD_LABEL[moodScore-1]}</p>}
            <textarea value={moodNote} onChange={e => setMoodNote(e.target.value)} rows={2} placeholder="Any comments? (optional)" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-4 focus:ring-2 focus:ring-blue-500"/>
            <button onClick={handleMoodSubmit} disabled={!moodScore || moodSubmitting} className="w-full py-2.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 mb-2">
              {moodSubmitting ? 'Submitting...' : 'Submit'}
            </button>
            {moodStatus?.policy?.allowSkip && moodStatus?.remainingSkips > 0 && (
              <button onClick={handleMoodSkip} disabled={moodSubmitting} className="w-full py-2 text-gray-500 text-sm hover:text-gray-700">
                Skip ({moodStatus.remainingSkips} remaining)
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendancePage;
